import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getImageProcessingSettings,
  updateImageProcessingSettings,
} from "../api/imageProcessingSettingsApi";
import "../styles/ThemeSettingsPanel.css";

const MODULES = [
  { key: "inward_and_outward_files", label: "Inward / Outward", icon: "📄" },
  { key: "maintenance", label: "Maintenance", icon: "🔧" },
  { key: "certificate_templates", label: "Certificates", icon: "🏅" },
  { key: "cheques", label: "Cheques", icon: "🏦" },
  { key: "property_assets_images", label: "Property Assets", icon: "🏢" },
  { key: "payments", label: "Payments", icon: "💳" },
  { key: "users", label: "User Photos", icon: "👤" },
];

const MIN_MAX_BYTES = 1024;

const clampInt = (v, min, max) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, Math.trunc(n)));
};

const clampQuality = (v) => clampInt(v, 10, 100);
const clampMaxBytes = (v) => clampInt(v, MIN_MAX_BYTES, Number.MAX_SAFE_INTEGER);

const kbFromBytes = (bytes) => Math.max(1, Math.round(Number(bytes || 0) / 1024));

function QualityRing({ value, size = 52 }) {
  const pct = ((value - 10) / 90) * 100;
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  const hue = Math.round(120 * (pct / 100));
  return (
    <svg className="ip__ring" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="rgba(0,0,0,0.06)"
        strokeWidth={4}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={`hsl(${hue}, 72%, 48%)`}
        strokeWidth={4}
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset .35s ease, stroke .35s ease" }}
      />
      <text
        x={size / 2}
        y={size / 2}
        textAnchor="middle"
        dominantBaseline="central"
        className="ip__ring-text"
        style={{ fontSize: size < 44 ? 11 : 13 }}
      >
        {value}
      </text>
    </svg>
  );
}

export default function ThemeSettingsPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedKey, setSelectedKey] = useState(null);
  const editorRef = useRef(null);

  const [serverSettings, setServerSettings] = useState(null);
  const [draft, setDraft] = useState(null);

  const draftCategories = useMemo(() => {
    if (!draft?.default) return {};
    const out = {};
    for (const m of MODULES) {
      out[m.key] = draft.categories?.[m.key] || draft.default;
    }
    return out;
  }, [draft]);

  const selectedModule = useMemo(
    () => MODULES.find((m) => m.key === selectedKey) || null,
    [selectedKey],
  );

  useEffect(() => {
    if (!selectedKey || !editorRef.current) return;
    editorRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      setSuccess("");
      try {
        const payload = await getImageProcessingSettings();
        if (cancelled) return;
        setServerSettings(payload?.data || payload);
        const base = payload?.data || payload || {};
        setDraft({
          default: base.default,
          categories: { ...(base.categories || {}) },
        });
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load image settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const applyToAll = () => {
    const next = {
      webpQuality: clampQuality(draft.default.webpQuality),
      maxBytes: clampMaxBytes(draft.default.maxBytes),
    };
    setDraft((prev) => {
      const categories = {};
      for (const m of MODULES) categories[m.key] = next;
      return { ...prev, default: next, categories };
    });
  };

  const setCategoryValue = (key, patch) => {
    setDraft((prev) => ({
      ...prev,
      categories: {
        ...(prev.categories || {}),
        [key]: { ...(prev.categories?.[key] || prev.default), ...patch },
      },
    }));
  };

  const resetModuleToGlobal = (key) => {
    setCategoryValue(key, {
      webpQuality: clampQuality(draft.default.webpQuality),
      maxBytes: clampMaxBytes(draft.default.maxBytes),
    });
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const categories = {};
      for (const m of MODULES) {
        const c = draftCategories[m.key];
        categories[m.key] = {
          maxBytes: clampMaxBytes(c.maxBytes),
          webpQuality: clampQuality(c.webpQuality),
        };
      }
      const res = await updateImageProcessingSettings({
        default: {
          maxBytes: clampMaxBytes(draft.default.maxBytes),
          webpQuality: clampQuality(draft.default.webpQuality),
        },
        categories,
      });
      setServerSettings(res?.data || res);
      setSuccess("Saved — new uploads will use these WebP settings.");
    } catch (e) {
      setError(e?.message || "Failed to save image settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !draft?.default) {
    return (
      <div className="ip">
        <div className="ip__mesh" aria-hidden />
        <div className="ip__loading">
          <span className="ip__loading-bar" />
          {loading ? "Loading image policy…" : "Image processing settings are not configured yet."}
        </div>
      </div>
    );
  }

  const defQ = clampInt(draft.default.webpQuality, 10, 100);
  const defKB = kbFromBytes(draft.default.maxBytes);

  const sel = selectedModule ? draftCategories[selectedModule.key] : null;
  const selQ = sel ? clampInt(sel.webpQuality, 10, 100) : 0;
  const selKB = sel ? kbFromBytes(sel.maxBytes) : 0;
  const usesGlobal =
    selectedModule &&
    selQ === defQ &&
    selKB === defKB;

  return (
    <div className="ip" aria-live="polite">
      <div className="ip__mesh" aria-hidden />

      <header className="ip__head">
        <div className="ip__head-main">
          <p className="ip__eyebrow">Preferences</p>
          <h2 className="ip__title">Image Processing</h2>
          <p className="ip__sub">
            Every upload is converted to <strong>WebP</strong> on the fly.
            Only <em>quality</em> is adjusted — dimensions stay untouched,
            edges preserved.
          </p>
        </div>

        <div className="ip__signals">
          <div className="ip__signal">
            <span className="ip__signal-label">Format</span>
            <strong>
              <span className="ip__signal-dot" />
              WebP
            </strong>
          </div>
          <div className="ip__signal">
            <span className="ip__signal-label">Resize</span>
            <strong className="ip__signal--off">Off</strong>
          </div>
        </div>
      </header>

      {error ? (
        <div className="ip__toast ip__toast--error" role="alert">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="ip__toast ip__toast--ok" role="status">
          {success}
        </div>
      ) : null}

      <div className="ip__stack">
        <section className="ip__card ip__card--global">
          <div className="ip__card-stripe" />
          <div className="ip__card-body">
            <div className="ip__global-left">
              <QualityRing value={defQ} size={64} />
              <div className="ip__global-copy">
                <h3>Global baseline</h3>
                <p>Applies to any module without a custom override.</p>
              </div>
            </div>

            <div className="ip__global-controls">
              <div className="ip__ctrl">
                <label className="ip__ctrl-label">Quality</label>
                <div className="ip__ctrl-row">
                  <input
                    type="range"
                    min={10}
                    max={100}
                    value={defQ}
                    className="ip__slider"
                    onChange={(e) =>
                      setDraft((p) => ({
                        ...p,
                        default: {
                          ...p.default,
                          webpQuality: clampInt(e.target.value, 10, 100),
                        },
                      }))
                    }
                  />
                  <span className="ip__ctrl-num">{defQ}</span>
                </div>
              </div>
              <div className="ip__ctrl">
                <label className="ip__ctrl-label">Max size</label>
                <div className="ip__ctrl-row">
                  <input
                    type="number"
                    min={1}
                    max={5120}
                    step={1}
                    value={defKB}
                    className="ip__num-input"
                    onChange={(e) => {
                      const kb = clampInt(e.target.value, 1, 5120);
                      setDraft((p) => ({
                        ...p,
                        default: { ...p.default, maxBytes: kb * 1024 },
                      }));
                    }}
                  />
                  <span className="ip__ctrl-unit">KB</span>
                </div>
              </div>
              <button
                type="button"
                className="ip__apply-btn"
                onClick={applyToAll}
                disabled={saving}
              >
                Sync to all
              </button>
            </div>
          </div>
        </section>

        <section className="ip__modules">
          <h3 className="ip__section-title">Per-module overrides</h3>
          <p className="ip__section-hint">
            Click a module below — its editor opens here. Then press <strong>Save policy</strong>.
          </p>

          {selectedModule && sel ? (
            <div
              ref={editorRef}
              className="ip__editor"
              role="region"
              aria-label={`${selectedModule.label} settings`}
            >
              <div className="ip__editor-head">
                <div className="ip__editor-identity">
                  <span className="ip__editor-icon">{selectedModule.icon}</span>
                  <div>
                    <h4 className="ip__editor-title">{selectedModule.label}</h4>
                    <p className="ip__editor-sub">
                      {usesGlobal
                        ? "Using global baseline — adjust below to override."
                        : "Custom policy for this module."}
                    </p>
                  </div>
                </div>
                <div className="ip__editor-actions">
                  <button
                    type="button"
                    className="ip__editor-reset"
                    onClick={() => resetModuleToGlobal(selectedModule.key)}
                    disabled={usesGlobal}
                  >
                    Reset to global
                  </button>
                  <button
                    type="button"
                    className="ip__editor-close"
                    onClick={() => setSelectedKey(null)}
                    aria-label="Close editor"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="ip__editor-body">
                <div className="ip__editor-metric">
                  <QualityRing value={selQ} size={72} />
                  <span className="ip__editor-metric-label">WebP quality</span>
                </div>

                <div className="ip__editor-controls">
                  <div className="ip__ctrl ip__ctrl--wide">
                    <label className="ip__ctrl-label" htmlFor={`ip-q-${selectedModule.key}`}>
                      Quality
                    </label>
                    <div className="ip__ctrl-row">
                      <input
                        id={`ip-q-${selectedModule.key}`}
                        type="range"
                        min={10}
                        max={100}
                        value={selQ}
                        className="ip__slider"
                        onChange={(e) =>
                          setCategoryValue(selectedModule.key, {
                            webpQuality: clampInt(e.target.value, 10, 100),
                          })
                        }
                      />
                      <span className="ip__ctrl-num ip__ctrl-num--lg">{selQ}</span>
                    </div>
                    <div className="ip__ctrl-hints">
                      <span>Smaller file</span>
                      <span>Sharper image</span>
                    </div>
                  </div>

                  <div className="ip__ctrl ip__ctrl--size">
                    <label className="ip__ctrl-label" htmlFor={`ip-kb-${selectedModule.key}`}>
                      Max file size
                    </label>
                    <div className="ip__size-box">
                      <input
                        id={`ip-kb-${selectedModule.key}`}
                        type="number"
                        min={1}
                        max={5120}
                        step={1}
                        value={selKB}
                        className="ip__num-input ip__num-input--lg"
                        onChange={(e) => {
                          const v = clampInt(e.target.value, 1, 5120);
                          setCategoryValue(selectedModule.key, { maxBytes: v * 1024 });
                        }}
                      />
                      <span className="ip__ctrl-unit">KB</span>
                    </div>
                    <p className="ip__ctrl-note">Dimensions are never reduced — only quality.</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p className="ip__editor-placeholder">
              Select a module below to edit its WebP quality and size limit.
            </p>
          )}

          <div className="ip__grid">
            {MODULES.map((m, i) => {
              const c = draftCategories[m.key];
              const q = clampInt(c.webpQuality, 10, 100);
              const kb = kbFromBytes(c.maxBytes);
              const active = selectedKey === m.key;
              const custom = q !== defQ || kb !== defKB;
              return (
                <button
                  type="button"
                  key={m.key}
                  className={`ip__tile${active ? " is-active" : ""}${custom ? " is-custom" : ""}`}
                  style={{ "--ip-i": i }}
                  onClick={() => setSelectedKey(m.key)}
                  aria-pressed={active}
                >
                  <span className="ip__tile-icon">{m.icon}</span>
                  <span className="ip__tile-name">{m.label}</span>
                  <span className="ip__tile-meta">
                    <QualityRing value={q} size={34} />
                    <span className="ip__tile-pills">
                      <span className="ip__pill">Q {q}</span>
                      <span className="ip__pill">{kb} KB</span>
                    </span>
                  </span>
                  {custom ? <span className="ip__tile-badge">Custom</span> : null}
                </button>
              );
            })}
          </div>
        </section>
      </div>

      <footer className="ip__footer">
        {serverSettings?.updatedAt ? (
          <span className="ip__footer-ts">
            Last saved {new Date(serverSettings.updatedAt).toLocaleString()}
          </span>
        ) : null}
        <button type="button" className="ip__save-btn" onClick={onSave} disabled={saving}>
          {saving ? (
            <>
              <span className="ip__save-spinner" />
              Saving…
            </>
          ) : (
            "Save policy"
          )}
        </button>
      </footer>
    </div>
  );
}
